import { isMobile } from '../helpers/common'
import { ButtonEntry, Entry, HeaderMenuItem } from '../interfaces/header'
import common from '../lang/common.json'
import { headerBreakPoint } from './breakPoints'

// headerBreakpoint 999px (see /src/constants/breakPoints.ts)
export const headerHeight = isMobile(headerBreakPoint) ? 56 : 70 // px

const enProductMenuItems: HeaderMenuItem[] = [
  {
    columnName: 'Platform',
    columnItems: [
      {
        title: 'Overview',
        description: 'A flexible platform built for the new way of doing product management.',
        url: '/product/',
      },
      {
        title: 'Security',
        description: 'airfocus is designed in accordance with the highest security standards.',
        url: '/security/',
      },
      {
        title: 'Enterprise',
        description: 'The product management platform designed for modern enterprises.',
        url: '/enterprise/',
      },
      {
        title: 'AI Assist',
        description: 'Unlock the power of AI for product managers.',
        url: '/product/ai/',
      },
    ],
  },
  {
    columnName: 'Features',
    columnItems: [
      {
        title: 'Roadmaps',
        description: 'Create alignment and set  <br class="hide-on-small-medium-large" /> a clear direction.',
        url: '/product/roadmaps/',
      },
      {
        title: 'Prioritization',
        description: 'Prioritize your strategy, discovery, and execution.',
        url: '/product/prioritization/',
      },
      {
        title: 'Feedback & Insights',
        description: 'Understand customer needs and solve the right problems.',
        url: '/product/insights/',
      },
      {
        title: 'Objectives & OKRs',
        description: 'Manage OKRs in airfocus and connect them to your roadmaps.',
        url: '/product/objectives-okrs/',
      },
      {
        title: 'Capacity Planning',
        description: 'Manage high-level capacity planning and resource allocation.',
        url: '/product/capacity-planning/',
      },
    ],
  },
  {
    columnName: 'Integrations',
    columnItems: [
      {
        title: 'Jira',
        description: 'Align your development team with product discovery.',
        url: '/integrations/jira/',
      },
      {
        title: 'Azure DevOps',
        description: 'Integrate discovery activities with your product execution.',
        url: '/integrations/azure-devops/',
      },
      {
        title: 'Slack',
        description: 'Push Slack messages to airfocus and process them as feedback.',
        url: '/integrations/slack/',
      },
      {
        linkText: 'All integrations',
        url: '/integrations/',
      },
    ],
  },
  {
    columnName: 'Product Experts',
    columnItems: [],
    expertsBlock: {
      images: ['about-us/maria.webp', 'about-us/victor.webp', 'about-us/julia.webp'],
      title: 'Book a call with a product consultant',
      description: 'Learn how the airfocus end-to-end product management platform can help you build better products.',
    },
  },
]

const frProductMenuItems: HeaderMenuItem[] = [
  {
    columnName: 'Platforme',
    columnItems: [
      {
        title: 'Découvrir notre produit',
        description: 'La plateforme flexible conçue pour une nouvelle façon de gérer vos produits.',
        url: '/fr/product/',
      },
      // Make sure to have the LANG hint as last item
      {
        title: 'Menu complet',
        description: "Trouvez (encore) plus d'informations sur la version anglaise de notre site web.",
        url: 'https://airfocus.com/',
      },
    ],
  },
  {
    columnName: 'Fonctionnalités',
    columnItems: [
      {
        title: 'Roadmap',
        description: "Alignez les équipes et définissez une stratégie claire pour toute l'entreprise.",
        url: '/fr/product/roadmaps/',
      },
      {
        title: 'Priorisation',
        description: "Donnez la priorité à votre stratégie, à la découverte, et à l'exécution.",
        url: '/fr/product/prioritization/',
      },
      {
        title: 'Gestion des retours utilisateurs',
        description: 'Comprenez les réels besoins clients afin de résoudre leurs vrais problèmes.',
        url: '/fr/product/insights/',
      },
      {
        title: 'Objectifs & OKRs',
        description: 'Gérez vos Objectifs et Résultats Clés avec airfocus et connectez-les à votre roadmap.',
        url: '/fr/product/objectives-okrs/',
      },
      {
        title: 'Portail',
        description: "Engagez les utilisateurs et les décisionnaires au sein d'un portail public.",
        url: '/fr/product/portal/',
      },
    ],
  },
  {
    columnName: 'Consultants Produits',
    columnItems: [],
    expertsBlock: {
      images: ['about-us/maria.webp', 'about-us/victor.webp', 'about-us/julia.webp'],
      title: 'Réservez un appel avec un de nos experts',
      description:
        'Découvrez comment la plateforme de gestion de produits airfocus vous aide à concevoir de meilleurs produits de bout-en-bout.',
    },
  },
]

const deProductMenuItems: HeaderMenuItem[] = [
  {
    columnName: 'Platform',
    columnItems: [
      {
        title: 'Überblick',
        description: 'Die flexible Plattform für die neue Art des Produktmanagements.',
        url: '/de/product/',
      },
      {
        title: 'Alle Menüpunkte',
        description: 'Finde mehr Informationen auf der englischen Version unserer Website.',
        url: 'https://airfocus.com/',
      },
    ],
  },
  {
    columnName: 'Features',
    columnItems: [
      {
        title: 'Roadmaps',
        description: 'Richte dein Team auf ein klares Ziel aus.',
        url: '/de/product/roadmaps/',
      },
      {
        title: 'Priorisierung',
        description:
          'Priosisiere Strategie, Problem<span class="hide-on-small-medium-large">-</span>findung und Durchführung.',
        url: '/de/product/prioritization/',
      },
      {
        title: 'Feedback & Erkenntnisse',
        description: 'Verstehe Kundenbedürfnisse und löse die richtigen Probleme.',
        url: '/de/product/insights/',
      },
      {
        title: 'Objectives & OKRs',
        description: 'Verwalte eure OKRs und verbinde sie mit deinen Roadmaps.',
        url: '/de/product/objectives-okrs/',
      },
      {
        title: 'Portal',
        description: 'Sei über dein öffentliches Portal im Kontakt mit Nutzenden.',
        url: '/de/product/portal/',
      },
    ],
  },
  {
    columnName: 'Product Experts',
    columnItems: [],
    expertsBlock: {
      images: ['about-us/maria.webp', 'about-us/victor.webp', 'about-us/julia.webp'],
      title: 'Book a call with a product consultant',
      description: 'Learn how the airfocus end-to-end product management platform can help you build better products.',
    },
  },
]

const enResourcesMenuItems: HeaderMenuItem[] = [
  {
    columnName: 'Help',
    columnItems: [
      {
        title: 'Academy',
        description: 'See the best ways to use airfocus in modern product management.',
        url: '/academy/',
      },
      {
        title: 'Help center',
        description: 'Understand airfocus in full detail with step-by-step instructions.',
        url: 'https://help.airfocus.com/',
      },
      // {
      //   title: 'Product walkthroughs',
      //   description: 'Join our weekly webinars and experience the platform.',
      //   url: '/resources/events/learn-airfocus/',
      // },
    ],
  },
  {
    columnName: 'Learn',
    columnItems: [
      {
        title: 'eBooks & reports',
        description: 'Deep dive into high-quality product management eBooks.',
        url: '/resources/ebooks/',
      },
      {
        title: '5-min guides',
        description: 'The most condensed content on product management topics.',
        url: '/resources/5-min-guides/',
      },
      {
        title: 'Checklists',
        description: 'Our collection of actionable downloadable checklists.',
        url: '/resources/checklists/',
      },
      {
        title: 'Events & webinars',
        description: 'Discuss trends and the future of product management with us.',
        url: '/resources/events/',
      },
      {
        title: 'Blog',
        description: 'Tips and inspiration on all <br class="hide-on-small-medium-large" /> things product management.',
        url: '/blog/',
      },
      {
        title: 'Case studies',
        description: 'Learn how, when, and why teams are using airfocus.',
        url: '/resources/case-studies/',
      },
      {
        title: 'Product Learn',
        description: 'Learn from industry experts how to level up your PM career.',
        url: '/product-learn/',
      },
    ],
  },
  {
    columnName: 'Featured',
    columnItems: [],
  },
]

export const getEnEntriesNavigation = (): Entry[] => {
  return [
    {
      label: 'Why airfocus',
      url: '/the-airfocus-way/',
    },
    {
      label: 'Product',
      type: 'dropdown',
      dropdownItems: enProductMenuItems,
    },
    {
      label: 'Pricing',
      url: '/pricing/',
      preferHashOnCurrentPage: true,
    },
    {
      label: 'Templates',
      url: '/templates/',
    },
    {
      label: 'Resources',
      type: 'dropdown',
      dropdownItems: enResourcesMenuItems,
    },
  ]
}

export const getFrEntriesNavigation = (): Entry[] => {
  return [
    {
      label: 'Pourquoi airfocus',
      url: '/fr/the-airfocus-way/',
    },
    {
      label: 'Produit',
      type: 'dropdown',
      dropdownItems: frProductMenuItems,
    },
    {
      label: 'Tarifs',
      url: '/fr/pricing/',
      preferHashOnCurrentPage: true,
    },
  ]
}

export const getDeEntriesNavigation = (): Entry[] => {
  return [
    {
      label: 'Wieso airfocus',
      url: '/de/the-airfocus-way/',
    },
    {
      label: 'Produkt',
      type: 'dropdown',
      dropdownItems: deProductMenuItems,
    },
    {
      label: 'Preise',
      url: '/de/pricing/',
      preferHashOnCurrentPage: true,
    },
  ]
}

export const getEnEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Login',
      url: 'https://app.airfocus.com/login',
    },
    {
      ...getEnBookADemoButton(),
    },
  ]
}

export const getFrEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Connexion',
      url: 'https://app.airfocus.com/login',
    },
    {
      ...getFrBookADemoButton(),
    },
  ]
}

export const getDeEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Anmelden',
      url: 'https://app.airfocus.com/login',
    },
    {
      ...getDeBookADemoButton(),
    },
  ]
}

export const getEnTryItFreeButton = (): ButtonEntry => ({
  type: 'button',
  label: common.trialText.en,
  showInDistractionFreeMode: true,
})

export const getFrTryItFreeButton = (): ButtonEntry => ({
  type: 'button',
  label: common.trialText.fr,
  showInDistractionFreeMode: true,
})

export const getDeTryItFreeButton = (): ButtonEntry => ({
  type: 'button',
  label: common.trialText.de,
  showInDistractionFreeMode: true,
})

export const getEnBookADemoButton = (): ButtonEntry => ({
  type: 'button',
  label: common.demoText.en,
  url: common.demoLink.en,
})

export const getFrBookADemoButton = (): ButtonEntry => ({
  type: 'button',
  label: common.demoText.fr,
  url: common.demoLink.fr,
})

export const getDeBookADemoButton = (): ButtonEntry => ({
  type: 'button',
  label: common.demoText.de,
  url: common.demoLink.de,
})

export const getEnLearnHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Become a writer',
      url: '/product-learn/become-a-writer/',
      emoji: '✏️',
    },
    {
      ...getEnBookADemoButton(),
    },
  ]
}

export const getFrLearnHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Devenir un auteur',
      url: '/product-learn/become-a-writer/',
      emoji: '✏️',
    },
    {
      ...getFrBookADemoButton(),
    },
  ]
}

export const getDeLearnHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      label: 'Werde Autor',
      url: '/product-learn/become-a-writer/',
      emoji: '✏️',
    },
    {
      ...getDeBookADemoButton(),
    },
  ]
}

export const getEnAdPageHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      ...getEnBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}

export const getFrAdPageHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      ...getFrBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}

export const getDeAdPageHeaderEntriesLogin = (): Entry[] => {
  return [
    {
      ...getDeBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}

export const getEnSearchHeaderEntriesLogin = (): Entry[] => {
  return [
    // when renaming please be careful and urgently update Header.tsx as well
    { label: 'search', type: 'search' },
    {
      ...getEnBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}

export const getFrSearchHeaderEntriesLogin = (): Entry[] => {
  return [
    // when renaming please be careful and urgently update Header.tsx as well
    { label: 'search', type: 'search' },
    {
      ...getFrBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}

export const getDeSearchHeaderEntriesLogin = (): Entry[] => {
  return [
    // when renaming please be careful and urgently update Header.tsx as well
    { label: 'search', type: 'search' },
    {
      ...getDeBookADemoButton(),
      showInDistractionFreeMode: true,
    },
  ]
}
